import {
  mudarStatusDoVeiculoSchema,
  ROTULO_DO_STATUS_DE_VEICULO,
  STATUS_CONTROLADOS_PELA_RESERVA,
  STATUS_OPERACIONAIS,
  type StatusDoVeiculo,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import { identificacaoDoVeiculo, useMudarStatus, type Veiculo } from './api'
import { BadgeDeStatusDoVeiculo } from './badges'

/**
 * Mudança de status do veículo.
 *
 * ---------------------------------------------------------------------------
 * O MOTIVO É O PRODUTO DESTA TELA, NÃO O STATUS
 * ---------------------------------------------------------------------------
 * O status novo o gestor descobre olhando o carro. O que ninguém reconstrói
 * quatro meses depois é POR QUE ele ficou nove dias na funilaria — e é essa a
 * pergunta que aparece quando se decide renovar ou vender o ativo. Por isso o
 * motivo é obrigatório no contrato (`mudarStatusDoVeiculoSchema`), e por isso a
 * interface diz em voz alta o que vai ser feito com o que a pessoa escrever: a
 * frase vira linha permanente da trilha, com autor e horário, e não é editável
 * depois.
 *
 * ---------------------------------------------------------------------------
 * RESERVADO E EM_LOCAÇÃO NÃO ESTÃO NA LISTA
 * ---------------------------------------------------------------------------
 * Eles são escritos pela reserva (Fase 4). Oferecê-los aqui deixaria o status do
 * carro e o da reserva divergirem no primeiro clique — a API recusa, mas um
 * caminho que só termina em 422 não deveria existir na tela. A lista sai de
 * `STATUS_OPERACIONAIS`, que é a mesma constante que o servidor usa para
 * recusar.
 *
 * O status ATUAL também some da lista: repetir o status não é mudança, e a API
 * responde 409 justamente para não gravar linha de histórico sem fato.
 */

const CAMPOS = ['status', 'motivo'] as const

/** Os rótulos de quem manda em RESERVADO/EM_LOCAÇÃO, montados do enum. */
const CONTROLADOS_PELA_RESERVA = STATUS_CONTROLADOS_PELA_RESERVA.map(
  (status) => ROTULO_DO_STATUS_DE_VEICULO[status].rotulo,
).join(' e ')

interface FormularioDeStatus {
  status: StatusDoVeiculo
  motivo: string
}

export interface DialogoDeStatusProps {
  /** `null` mantém o diálogo fechado — ele abre com um veículo, não com um booleano. */
  readonly veiculo: Veiculo | null
  readonly aoFechar: () => void
}

export function DialogoDeStatus({ veiculo, aoFechar }: DialogoDeStatusProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const mudar = useMudarStatus()

  const opcoes = useMemo(
    () => STATUS_OPERACIONAIS.filter((status) => status !== veiculo?.statusAtual),
    [veiculo],
  )

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormularioDeStatus>({ defaultValues: { status: 'DISPONIVEL', motivo: '' } })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir o
  // segundo veículo mostraria o motivo digitado para o primeiro — e o motivo é
  // exatamente o campo que não pode ser reaproveitado por engano.
  useEffect(() => {
    if (veiculo === null) {
      return
    }

    setErroGeral(null)
    reset({ status: opcoes[0] ?? 'DISPONIVEL', motivo: '' })
  }, [veiculo, opcoes, reset])

  const ocupado = mudar.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (veiculo === null) {
      return
    }

    const entrada = { status: valores.status, motivo: valores.motivo }
    const validado = mudarStatusDoVeiculoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    // `mudarStatusDoVeiculoSchema` não transforma dinheiro nem data — só apara
    // o texto do motivo. Aqui `validado.data` é seguro (e é o valor já aparado).
    mudar.mutate(
      { id: veiculo.id, dados: validado.data },
      {
        onSuccess: (atualizado) => {
          toast.success('Status atualizado', {
            description: `${identificacaoDoVeiculo(atualizado)} passou para ${ROTULO_DO_STATUS_DE_VEICULO[atualizado.statusAtual].rotulo}.`,
          })
          aoFechar()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
        },
      },
    )
  })

  return (
    <Dialog
      open={veiculo !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mudar status</DialogTitle>
          <DialogDescription>
            {veiculo === null
              ? 'Selecione um veículo.'
              : `${identificacaoDoVeiculo(veiculo)} — a mudança entra na trilha do veículo com o seu nome e o horário.`}
          </DialogDescription>
        </DialogHeader>

        {veiculo === null ? null : (
          <form
            noValidate
            onSubmit={(evento) => {
              void aoEnviar(evento)
            }}
            className="space-y-4"
          >
            {erroGeral === null ? null : (
              <Alert variant="danger">
                <AlertDescription>{erroGeral}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5">
              <span className="text-sm text-fg-secondary">Status atual</span>
              <BadgeDeStatusDoVeiculo status={veiculo.statusAtual} />
            </div>

            <Campo
              rotulo="Novo status"
              obrigatorio
              erro={errors.status?.message}
              dica={`${CONTROLADOS_PELA_RESERVA} não aparecem aqui: quem os escreve é a reserva, não a operação.`}
            >
              {(campo) => (
                <Select {...campo} {...register('status')} disabled={ocupado}>
                  {opcoes.map((status) => (
                    <option key={status} value={status}>
                      {ROTULO_DO_STATUS_DE_VEICULO[status].rotulo}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Motivo"
              obrigatorio
              erro={errors.motivo?.message}
              dica="Vira linha permanente da trilha e não pode ser corrigida depois. Escreva o que aconteceu, não o status: “batida no para-choque traseiro, funilaria da Rua X”."
            >
              {(campo) => (
                <Textarea
                  {...campo}
                  {...register('motivo', { required: 'descreva o motivo' })}
                  rows={3}
                  maxLength={500}
                  placeholder="O que aconteceu com este carro"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
                Cancelar
              </Button>
              <Button type="submit" disabled={ocupado}>
                {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                Registrar mudança
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
